/**
 * legalHoldStore.js
 * ──────────────────
 * GDPR Art. 17(3), exemptions from the right to erasure.
 *
 * The right to be forgotten does NOT apply when:
 *   (b) processing is necessary for compliance with a legal obligation
 *   (e) processing is necessary for the establishment, exercise or defence
 *       of legal claims
 *
 * In practice, this means a DPO must be able to mark a data subject as
 * "on legal hold", typically because the company is in litigation with or
 * about that person, or because a tax authority has subpoenaed records.
 * While on hold, auto-purge skips the subject and erasure requests are
 * automatically refused with a documented reason.
 *
 * Per-jurisdiction minimums
 * ──────────────────────────
 * Many EU member states impose retention minimums that override the tenant's
 * preference for a shorter window. Examples:
 *
 *   Germany:     §147 AO, 10 years for tax-relevant records
 *   France:      Code du travail, 5 years for personnel files
 *   Spain:       Estatuto de los Trabajadores, 4 years post-exit
 *   Italy:       D.Lgs. 231/2001, 10 years for compliance records
 *   UK:          PAYE, 6 years post-exit for tax
 *   Switzerland: OR Art. 957a, 10 years for accounting records
 *
 * Setting tenant retention below the legal minimum is a regulatory risk;
 * the UI warns the admin and (in production) requires a documented
 * acknowledgement before saving.
 */

const HOLD_KEY = 'clearequal.legalHolds.v1';
const EVENT = 'clearequal.legalHolds.changed';

// Minimum retention days per jurisdiction per data category.
// Sourced from the relevant national tax / employment law as of 2024-25.
// Always confirm with local counsel before deployment, these change.
export const JURISDICTION_MINIMUMS = {
  DE: {
    compensation_data: 3650, // §147 AO, 10 years
    contact_data:      0,
    audit_trail:       3650,
    info_requests:     1095, // good practice, 3 years parallel to ZPO statute of limitations
  },
  FR: {
    compensation_data: 1825, // Code du travail, 5 years
    contact_data:      0,
    audit_trail:       1825,
    info_requests:     1095,
  },
  ES: {
    compensation_data: 1460, // Estatuto de los Trabajadores, 4 years
    contact_data:      0,
    audit_trail:       1825,
    info_requests:     1095,
  },
  IT: {
    compensation_data: 3650, // D.Lgs. 231/2001, 10 years for compliance records
    contact_data:      0,
    audit_trail:       3650,
    info_requests:     1095,
  },
  UK: {
    compensation_data: 2190, // PAYE, 6 years post-exit
    contact_data:      0,
    audit_trail:       2555, // SOC 2 alignment 7 years
    info_requests:     1095,
  },
  CH: {
    compensation_data: 3650, // OR Art. 957a, 10 years
    contact_data:      0,
    audit_trail:       3650,
    info_requests:     1095,
  },
};

/**
 * Returns minimum retention days for a category in the given jurisdiction.
 * Returns 0 if no minimum is defined (admin can set whatever they want).
 */
export function getMinimumRetention(jurisdictionCode, category) {
  const j = JURISDICTION_MINIMUMS[jurisdictionCode];
  if (!j) return 0;
  return j[category] || 0;
}

/**
 * Validate a proposed retention policy against jurisdiction minimums.
 * Returns array of { category, requested, minimum, jurisdiction } warnings.
 */
export function validateRetentionPolicy(policy, jurisdictionCode) {
  const warnings = [];
  if (!jurisdictionCode || !JURISDICTION_MINIMUMS[jurisdictionCode]) return warnings;

  for (const [cat, cfg] of Object.entries(policy)) {
    const min = getMinimumRetention(jurisdictionCode, cat);
    if (min > 0 && cfg.retentionDays < min && cfg.action === 'delete') {
      warnings.push({
        category: cat,
        requested: cfg.retentionDays,
        minimum: min,
        jurisdiction: jurisdictionCode,
        severity: 'high',
        message: `Below legal minimum: ${jurisdictionCode} requires ${min} days (~${(min / 365).toFixed(1)} years) for ${cat}`,
      });
    }
  }
  return warnings;
}

// ─── Legal hold CRUD ────────────────────────────────────────────────────
function readHolds() {
  try {
    const raw = localStorage.getItem(HOLD_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeHolds(items) {
  try {
    localStorage.setItem(HOLD_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch (_) {}
}

/**
 * Place a data subject on legal hold.
 *
 * @param {Object} args
 * @param {string} args.subjectEmail - the data subject
 * @param {string} args.tenantId
 * @param {string} args.placedBy - user id of the DPO/admin placing the hold
 * @param {string} args.reason - documented justification (required)
 * @param {string} [args.expectedReleaseDate] - ISO date when the hold may be lifted
 * @param {string} [args.legalBasis] - one of: 'litigation' | 'tax_audit' | 'regulatory_inquiry' | 'criminal_investigation' | 'other'
 */
export function placeHold({
  subjectEmail,
  tenantId,
  placedBy,
  reason,
  expectedReleaseDate = null,
  legalBasis = 'other',
}) {
  if (!subjectEmail) throw new Error('subjectEmail is required');
  if (!reason || !reason.trim()) {
    throw new Error('A documented reason is required (Art. 17(3), must justify retention)');
  }
  if (!placedBy) throw new Error('placedBy (DPO user id) is required');

  const all = readHolds();
  const existing = all.find(
    (h) => h.subjectEmail === subjectEmail && h.tenantId === tenantId && h.status === 'active'
  );
  if (existing) {
    throw new Error(`Subject ${subjectEmail} is already on active hold`);
  }

  const hold = {
    id: `hold-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    subjectEmail,
    tenantId,
    placedBy,
    placedAt: new Date().toISOString(),
    reason,
    expectedReleaseDate,
    legalBasis,
    status: 'active',
  };
  all.push(hold);
  writeHolds(all);
  return hold;
}

/**
 * Release a hold. Required: the user releasing it must NOT be the same as
 * the user who placed it (two-person rule for sensitive lifecycle events).
 */
export function releaseHold(holdId, releasedBy, releaseNote = '') {
  const all = readHolds();
  const idx = all.findIndex((h) => h.id === holdId);
  if (idx === -1) throw new Error('Hold not found');

  const h = all[idx];
  if (h.status !== 'active') throw new Error('Hold is not active');
  if (h.placedBy === releasedBy) {
    throw new Error('A different user must release the hold (separation of duties)');
  }

  all[idx] = {
    ...h,
    status: 'released',
    releasedBy,
    releasedAt: new Date().toISOString(),
    releaseNote,
  };
  writeHolds(all);
  return all[idx];
}

export function listHolds(filter = {}) {
  let all = readHolds();
  if (filter.tenantId) all = all.filter((h) => h.tenantId === filter.tenantId);
  if (filter.status) all = all.filter((h) => h.status === filter.status);
  if (filter.subjectEmail) all = all.filter((h) => h.subjectEmail === filter.subjectEmail);
  return all;
}

/**
 * Returns true if the subject currently has an active legal hold.
 * Called by erasure execution to refuse purges, and by auto-purge to skip.
 */
export function isOnHold(subjectEmail, tenantId) {
  return readHolds().some(
    (h) => h.subjectEmail === subjectEmail && h.tenantId === tenantId && h.status === 'active'
  );
}

/**
 * Returns the active hold record (or null), used to populate the
 * "refusal reason" when an erasure is automatically refused.
 */
export function getActiveHold(subjectEmail, tenantId) {
  return readHolds().find(
    (h) => h.subjectEmail === subjectEmail && h.tenantId === tenantId && h.status === 'active'
  ) || null;
}

export function subscribe(callback) {
  const handler = () => callback();
  window.addEventListener(EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}
